import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarEvent, SleepDetailsByDate } from '@demind-inc/core';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';

import { metricsApi } from '../api';
import { eventsSnackBarAtom } from '../recoil';

interface UseUpdateSleepDetailsParams {
  userId: string;
  metricId: string;
  detailsByDate: SleepDetailsByDate[];
}

export function useUpdateSleepDetails() {
  const queryClient = useQueryClient();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);

  const updateSleepDetailMutation = useMutation({
    mutationFn: ({ userId, metricId, detailsByDate }: UseUpdateSleepDetailsParams) => {
      return metricsApi.updateSleepDetails(metricId, userId, detailsByDate);
    },
    onMutate: async ({ detailsByDate }) => {
      const hasUpdate = detailsByDate.some((item) => item.details);
      if (!hasUpdate) return {};
      setEventsSnackbar({ message: 'Event updating...', type: 'loading' });

      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.events'] });

      const queries = queryClient.getQueriesData({ queryKey: ['lifestack.calendar.events'] });
      const previousQueries: [QueryKey, CalendarEvent[]][] = [];

      queries.forEach(([queryKey, data]) => {
        if (data && Array.isArray(data)) {
          previousQueries.push([queryKey, data]);

          let updatedData = data;

          detailsByDate.forEach((item) => {
            if (!item.details) return;

            const date = item.date;
            const { sleepTime, awakeTime } = item.details;

            if (!sleepTime || !awakeTime) return;

            updatedData = updatedData.map((event: CalendarEvent) => {
              const isSleepEvent = event.autoScheduleFrom?.from === 'sleep';
              if (!isSleepEvent) return event;

              if (!dayjs(event.start?.date).isSame(date, 'day')) return event;

              return {
                ...event,
                start: {
                  ...event.start,
                  date: sleepTime,
                },
                end: {
                  ...event.end,
                  date: awakeTime,
                },
              };
            });
          });

          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { previousQueries };
    },
    onSuccess: (_, { detailsByDate }) => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.sleep.details'] });

      const hasUpdate = detailsByDate.some((item) => item.details);
      if (hasUpdate) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
        setEventsSnackbar({ message: 'Event updated', type: 'success' });
      }

      detailsByDate.forEach((item) => {
        const date = item.date;
        if (item.details) {
          queryClient.invalidateQueries({ queryKey: ['lifestack.circadian', { date }] });
        }
      });
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });

  return {
    updateSleepDetail: updateSleepDetailMutation.mutateAsync,
    ...updateSleepDetailMutation,
  };
}
